import axios from "../utils/axios";
import { t } from "i18next";
import { getErrorMsg } from "../helpers/common";
import { store } from "../store/store";
import
  {
    removeDownload,
    setError,
    setName,
    setStatus,
  } from "../store/reducers/downloadSlice";
import
  {
    DownloadJob,
    fetchDownloadJob,
  } from "../services/download/downloadJobService";

/**
 * Downloads are prepared by a background job, not streamed by the API.
 *
 * Pressing "download" creates (or re-reads) a zip job; the manager polls it
 * until it reports READY and then lets the browser fetch the presigned URL the
 * job carries. The zip never passes through this app: a satellite order can be
 * tens of GB, and holding that in a blob would take the tab down with it.
 */

// Orders are large; zipping one takes minutes, so poll gently and wait long.
// Asking every few seconds only spams the service — a zip that needs minutes
// will not be ready any sooner for being asked about more often.
const POLL_MS = 15000;
const MAX_WAIT_MS = 30 * 60 * 1000;

// How long a finished entry lingers in the tray before it disappears.
const CLEANUP_MS = 3000;

// How long the hidden iframe that starts the browser download stays mounted.
const FRAME_CLEANUP_MS = 60000;

interface DownloadTask
{
  id: string;
  name: string;
  /** Job URL — idempotent, so it is also the poll URL and the retry call. */
  url: string;
  abortController: AbortController | null;
}

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) =>
  {
    const timer = setTimeout(() =>
    {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    function onAbort()
    {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    }

    signal.addEventListener("abort", onAbort, { once: true });
  });

const isAbortError = (error: any): boolean =>
  error?.name === "AbortError" ||
  error?.name === "CanceledError" ||
  axios.isCancel?.(error) === true;

class DownloadManager
{
  private tasks: Map<string, DownloadTask> = new Map();

  /**
   * Start (or resume watching) the download job behind `file.url`. The tray
   * entry must already be in the store — callers dispatch `addDownloads` first.
   */
  async downloadFile(file: { id: string; name: string; url: string }): Promise<void>
  {
    if (this.tasks.has(file.id)) return;

    const abortController = new AbortController();

    this.tasks.set(file.id, {
      id: file.id,
      name: file.name,
      url: file.url,
      abortController,
    });

    await this.pollJob(file.id, file.url, abortController.signal);
  }

  /**
   * Run the job again after an error. The endpoint is idempotent and resets an
   * ERROR job back to PENDING, so this is the same call as the initial one.
   */
  retry(id: string): void
  {
    const file = store
      .getState()
      .download.downloadingFiles.find((f: any) => f.id === id);

    if (!file?.url) return;

    // A failed run leaves its task behind so the tray keeps the URL; drop it
    // before starting over, otherwise downloadFile() would bail out early.
    this.tasks.delete(id);
    store.dispatch(setStatus({ id, status: "preparing" }));
    this.downloadFile({ id, name: file.name, url: file.url });
  }

  cancelAll()
  {
    this.tasks.forEach((task) => task.abortController?.abort());
    this.tasks.clear();
  }

  cancelById(id: string)
  {
    const task = this.tasks.get(id);
    if (task)
    {
      task.abortController?.abort();
      this.tasks.delete(id);
    }
    store.dispatch(removeDownload(id));
  }

  isDownloading(id: string): boolean
  {
    return this.tasks.has(id);
  }

  // Poll the job until it is ready, fails, or we run out of patience.
  private async pollJob(id: string, url: string, signal: AbortSignal): Promise<void>
  {
    const deadline = Date.now() + MAX_WAIT_MS;

    try
    {
      while (Date.now() < deadline)
      {
        const job = await fetchDownloadJob(url, signal);

        if (job?.status === "READY")
        {
          this.startBrowserDownload(id, job);
          return;
        }

        if (job?.status === "ERROR")
        {
          this.fail(id, job?.note ?? t("Download failed"));
          return;
        }

        // PENDING = queued, PROCESSING = zipping. Both keep the tray busy.
        store.dispatch(
          setStatus({
            id,
            status: job?.status === "PROCESSING" ? "processing" : "preparing",
          }),
        );

        await sleep(POLL_MS, signal);
      }

      this.fail(id, t("Download preparation timed out"));
    } catch (error: any)
    {
      // Cancelling drops the tray entry in cancelById — nothing to report here.
      if (isAbortError(error)) return;

      console.error(error);
      // fetchDownloadJob already turns backend failures into a readable message;
      // a raw axios rejection (401 refresh gave up) still needs unwrapping.
      const note = error?.isAxiosError ? getErrorMsg(error) : error?.message;
      this.fail(id, note || t("Download failed"));
    }
  }

  private startBrowserDownload(id: string, job: DownloadJob)
  {
    // Prefer the presigned link (straight from storage); the proxy route only
    // exists for clients that cannot reach the object store at all.
    const url = job.download_url || job.proxy_url;

    if (!url)
    {
      this.fail(id, job.note ?? t("Download failed"));
      return;
    }

    const filename = this.filenameFromUrl(url);
    if (filename)
    {
      store.dispatch(setName({ id, name: filename }));
    }

    // Mark it done first: from here the browser owns the transfer, so the
    // "download in progress" unload guard has nothing left to protect.
    store.dispatch(setStatus({ id, status: "completed" }));

    this.openInBrowser(url);

    setTimeout(() =>
    {
      store.dispatch(removeDownload(id));
      this.tasks.delete(id);
    }, CLEANUP_MS);
  }

  private fail(id: string, note?: string)
  {
    store.dispatch(setError({ id, note }));
    // Keep the task out of the map so "Retry" can start a fresh run, but leave
    // the tray entry standing — it holds the URL that retry needs.
    this.tasks.delete(id);
  }

  /**
   * Hand the URL to the browser as-is.
   *
   * The link is AWS SigV4-signed: re-encoding it, reordering its query, or
   * attaching an Authorization header all break the signature (MinIO answers
   * 403). No `download` attribute either — the filename travels in the URL's
   * `response-content-disposition`, and the attribute is ignored cross-origin.
   *
   * A hidden iframe rather than a click on an <a>: an anchor navigates the tab,
   * and the browser fires `beforeunload` before it can tell the response is an
   * attachment — so the "Leave site?" prompt appears and answering "Cancel"
   * kills the download. The iframe navigates its own browsing context, so the
   * page never unloads and no prompt is raised. `target="_blank"` would dodge it
   * too, but the popup blocker eats it: by the time the zip is ready there is no
   * user gesture left to spend.
   */
  private openInBrowser(url: string)
  {
    const frame = document.createElement("iframe");
    frame.hidden = true;
    frame.style.display = "none";
    frame.src = url;

    document.body.appendChild(frame);

    // Give the response time to arrive and turn into a download; pulling the
    // frame out too early would cancel the request. Once the browser owns the
    // transfer, removing the frame no longer affects it.
    setTimeout(() => frame.remove(), FRAME_CLEANUP_MS);
  }

  // Read the server-chosen filename out of the presigned URL so the tray shows
  // the real name. Read-only: the URL itself is never rewritten.
  private filenameFromUrl(url: string): string | null
  {
    try
    {
      const disposition = new URL(url, window.location.href).searchParams.get(
        "response-content-disposition",
      );
      return disposition ? this.parseFilename(disposition) : null;
    } catch
    {
      return null;
    }
  }

  // Parse the filename out of a Content-Disposition header value.
  // Handles both `filename*=UTF-8''...` and `filename="..."` forms.
  parseFilename(disposition: string): string | null
  {
    if (!disposition) return null;

    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch)
    {
      try
      {
        return decodeURIComponent(utfMatch[1].trim().replace(/^["']|["']$/g, ""));
      } catch
      {
        return utfMatch[1].trim().replace(/^["']|["']$/g, "");
      }
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch) return asciiMatch[1].trim();

    return null;
  }
}

export const downloadManager = new DownloadManager();
